import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchVoidedOrders,
  restoreVoidedOrder,
  VOID_RESTORE_DAYS,
  type Order,
} from '@/lib/data';

/** Recycle bin for bills a manager voided on the POS. They're hidden from the
 *  POS and restorable here for VOID_RESTORE_DAYS after they were
 *  voided. The owner can review each one and restore it back to a live sale. */
export default function VoidedBills({ restaurantId }: { restaurantId: string }) {
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const rows = await fetchVoidedOrders(restaurantId, { limit: 500 });
      setOrders(rows);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load voided bills.');
    }
  }, [restaurantId]);

  useEffect(() => {
    load();
  }, [load]);

  const restore = useCallback(
    async (o: Order) => {
      const label = o.billNo || `₹${o.total.toFixed(0)}`;
      if (!window.confirm(`Restore bill ${label}? It goes back into sales and reappears on the POS.`)) return;
      setBusyId(o.id);
      try {
        await restoreVoidedOrder(o.id);
        setOrders((prev) => (prev ? prev.filter((x) => x.id !== o.id) : prev));
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not restore the bill.');
      } finally {
        setBusyId(null);
      }
    },
    [],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return orders ?? [];
    return (orders ?? []).filter(
      (o) =>
        (o.billNo ?? '').toLowerCase().includes(q) ||
        o.customer.toLowerCase().includes(q) ||
        (o.voidedBy ?? '').toLowerCase().includes(q) ||
        (o.voidReason ?? '').toLowerCase().includes(q) ||
        o.total.toFixed(0).includes(q),
    );
  }, [orders, search]);

  const loading = orders === null && !error;

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
        <i className="ri-delete-bin-6-line text-amber-600 mt-0.5"></i>
        <p className="text-sm text-amber-800">
          Bills voided on the POS land here. They're out of your sales and GST totals and hidden from the
          POS. Restore one to put it back as a live sale — possible for{' '}
          <span className="font-semibold">{VOID_RESTORE_DAYS} days</span> after it was voided. The bill
          itself is kept for 6 years, as GST record-keeping requires; it just leaves this list.
        </p>
      </div>

      <div className="relative">
        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400"></i>
        <input
          type="text"
          placeholder="Search bill no, customer, who voided, reason…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 pr-4 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 w-80"
        />
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-background-100 border-b border-background-200">
              <tr>
                {['Bill No', 'Type', 'Customer', 'Amount', 'Voided by', 'Reason', 'Voided on', 'Purges in', ''].map(
                  (h, i) => (
                    <th
                      key={h || i}
                      className={`px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap ${
                        h === 'Amount' ? 'text-right' : 'text-left'
                      }`}
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200">
              {loading && (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-foreground-400">
                    Loading voided bills…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-foreground-400">
                    {search.trim() ? 'No voided bill matches your search.' : 'Nothing in the recycle bin.'}
                  </td>
                </tr>
              )}
              {filtered.map((o) => (
                <tr key={o.id} className="hover:bg-background-100/60">
                  <td className="px-4 py-3 font-semibold text-foreground-800 whitespace-nowrap">
                    {o.billNo || '—'}
                  </td>
                  <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">{o.type}</td>
                  <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">{o.customer || '—'}</td>
                  <td className="px-4 py-3 text-right font-medium text-foreground-800 whitespace-nowrap">
                    ₹{o.total.toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">{o.voidedBy || '—'}</td>
                  <td className="px-4 py-3 text-foreground-600 max-w-[16rem] truncate" title={o.voidReason ?? ''}>
                    {o.voidReason || '—'}
                  </td>
                  <td className="px-4 py-3 text-foreground-500 whitespace-nowrap">{fmtDate(o.voidedAt)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <PurgeCountdown voidedAt={o.voidedAt} />
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => restore(o)}
                      disabled={busyId === o.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-primary-300 text-primary-600 hover:bg-primary-50 disabled:opacity-50 cursor-pointer"
                    >
                      <i className="ri-arrow-go-back-line"></i>
                      {busyId === o.id ? 'Restoring…' : 'Restore'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function fmtDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Days left to RESTORE this bill. Red in the last week. The row itself is
 *  retained for 6 years either way — this is the undo window, not a delete. */
function PurgeCountdown({ voidedAt }: { voidedAt?: string | null }) {
  if (!voidedAt) return <span className="text-foreground-400">—</span>;
  const purgeAt = new Date(voidedAt).getTime() + VOID_RESTORE_DAYS * 86_400_000;
  const daysLeft = Math.ceil((purgeAt - Date.now()) / 86_400_000);
  if (daysLeft <= 0) return <span className="text-foreground-400">restore window closed</span>;
  return (
    <span className={daysLeft <= 7 ? 'text-red-600 font-medium' : 'text-foreground-500'}>
      {daysLeft} day{daysLeft === 1 ? '' : 's'}
    </span>
  );
}
